/**
 * YouTube Library Page
 * 
 * Browse YouTube playlists, liked videos, and search for music
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { youtubeAuth, youtubeAPI } from '../services/youtube';
import YouTubePlayer from '../components/YouTubePlayer';
import './YouTubeLibrary.css';

function YouTubeLibrary({ user }) {
  const navigate = useNavigate();
  
  // Auth state
  const [isConnected, setIsConnected] = useState(false);
  const [youtubeUser, setYoutubeUser] = useState(null);
  
  // Data state
  const [playlists, setPlaylists] = useState([]);
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [playlistItems, setPlaylistItems] = useState([]);
  const [likedVideos, setLikedVideos] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [loadingItems, setLoadingItems] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searching, setSearching] = useState(false);
  const [activeTab, setActiveTab] = useState('playlists'); // playlists, liked, search
  const [error, setError] = useState(null);
  
  // Player state
  const [currentVideo, setCurrentVideo] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [queue, setQueue] = useState([]);
  const [queueIndex, setQueueIndex] = useState(0);

  // Check connection and load data
  useEffect(() => {
    const initYouTube = async () => {
      if (!youtubeAuth.isAuthenticated()) {
        setIsConnected(false);
        setLoading(false);
        return;
      }

      setIsConnected(true);
      
      try {
        // Load user info and playlists in parallel
        const [userInfo, playlistsData, likedData] = await Promise.all([
          youtubeAuth.getUserInfo(),
          youtubeAPI.getMyPlaylists(),
          youtubeAPI.getLikedVideos(null, 20)
        ]);

        setYoutubeUser(userInfo);
        setPlaylists(playlistsData.items);
        setLikedVideos(likedData.items);
      } catch (err) {
        console.error('Error loading YouTube data:', err);
        setError(err.message);
        if (err.message.includes('expired')) {
          setIsConnected(false);
        }
      } finally {
        setLoading(false);
      }
    };

    initYouTube();
  }, []);

  // Load playlist items when playlist selected
  const loadPlaylistItems = useCallback(async (playlist) => {
    setLoadingItems(true);
    setSelectedPlaylist(playlist);
    setPlaylistItems([]);
    
    try {
      const data = await youtubeAPI.getPlaylistItems(playlist.id);
      setPlaylistItems(data.items);
    } catch (err) {
      console.error('Error loading playlist items:', err);
      setError(err.message);
    } finally {
      setLoadingItems(false);
    }
  }, []);

  // Search
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setSearching(true);
    setSearchResults([]);
    setActiveTab('search');

    try {
      const data = await youtubeAPI.search(searchQuery);
      setSearchResults(data.items);
    } catch (err) {
      console.error('Search error:', err);
      setError(err.message);
    } finally {
      setSearching(false);
    }
  };

  // Play a video
  const playVideo = (video, videoList = null) => {
    setCurrentVideo(video);
    setIsPlaying(true);
    
    if (videoList) {
      setQueue(videoList);
      setQueueIndex(videoList.findIndex(v => v.id === video.id));
    }
  };

  // Play all from playlist
  const playAll = (videos) => {
    if (videos.length === 0) return;
    setQueue(videos);
    setQueueIndex(0);
    setCurrentVideo(videos[0]);
    setIsPlaying(true);
  };

  // Next/Previous
  const handleNext = () => {
    if (queueIndex < queue.length - 1) {
      const nextIndex = queueIndex + 1;
      setQueueIndex(nextIndex);
      setCurrentVideo(queue[nextIndex]);
      setIsPlaying(true);
    }
  };

  const handlePrevious = () => {
    if (queueIndex > 0) {
      const prevIndex = queueIndex - 1;
      setQueueIndex(prevIndex);
      setCurrentVideo(queue[prevIndex]);
      setIsPlaying(true);
    }
  };

  // Connect YouTube
  const handleConnect = async () => {
    try {
      await youtubeAuth.signIn();
      window.location.reload(); // Reload to fetch data
    } catch (err) {
      setError(err.message);
    }
  };

  // Not connected view
  if (!isConnected && !loading) {
    return (
      <div className="youtube-library">
        <div className="youtube-not-connected">
          <div className="not-connected-content">
            <div className="youtube-logo">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
            </div>
            <h2>Connect YouTube</h2>
            <p>Sign in with your Google account to access your YouTube playlists and music</p>
            <button className="connect-btn" onClick={handleConnect}>
              Connect YouTube Account
            </button>
            <button className="back-btn" onClick={() => navigate('/services')}>
              ← Back to Services
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Loading view
  if (loading) {
    return (
      <div className="youtube-library">
        <div className="youtube-loading">
          <div className="loading-spinner">🎵</div>
          <p>Loading your YouTube library...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="youtube-library">
      {/* Header */}
      <header className="youtube-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/services')}>
            ← Back
          </button>
          <div className="youtube-branding">
            <svg viewBox="0 0 24 24" fill="currentColor" className="yt-icon">
              <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
            </svg>
            <h1>YouTube Library</h1>
          </div>
        </div>
        
        <div className="header-right">
          {youtubeUser && (
            <div className="user-info">
              {youtubeUser.thumbnail && (
                <img src={youtubeUser.thumbnail} alt="" className="user-avatar" />
              )}
              <span>{youtubeUser.title}</span>
            </div>
          )}
        </div>
      </header>

      {/* Error */}
      {error && (
        <div className="youtube-error">
          <span>⚠️ {error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Search Bar */}
      <div className="youtube-search-bar">
        <form onSubmit={handleSearch}>
          <input
            type="text"
            placeholder="Search YouTube Music..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <button type="submit" disabled={searching}>
            {searching ? '...' : '🔍'}
          </button>
        </form>
      </div>

      {/* Tabs */}
      <div className="youtube-tabs">
        <button
          className={`tab ${activeTab === 'playlists' ? 'active' : ''}`}
          onClick={() => setActiveTab('playlists')}
        >
          📁 Playlists ({playlists.length})
        </button>
        <button
          className={`tab ${activeTab === 'liked' ? 'active' : ''}`}
          onClick={() => setActiveTab('liked')}
        >
          ❤️ Liked Videos ({likedVideos.length})
        </button>
        {searchResults.length > 0 && (
          <button
            className={`tab ${activeTab === 'search' ? 'active' : ''}`}
            onClick={() => setActiveTab('search')}
          >
            🔍 Search Results ({searchResults.length})
          </button>
        )}
      </div>

      {/* Main Content */}
      <div className="youtube-content">
        {/* Sidebar: Playlists or Video List */}
        <div className="youtube-sidebar">
          {activeTab === 'playlists' && (
            <>
              <h3>Your Playlists</h3>
              <div className="playlist-list">
                {playlists.map(playlist => (
                  <div
                    key={playlist.id}
                    className={`playlist-item ${selectedPlaylist?.id === playlist.id ? 'selected' : ''}`}
                    onClick={() => loadPlaylistItems(playlist)}
                  >
                    <img 
                      src={playlist.thumbnail || '/placeholder-playlist.png'} 
                      alt="" 
                      className="playlist-thumb"
                    />
                    <div className="playlist-info">
                      <span className="playlist-title">{playlist.title}</span>
                      <span className="playlist-count">{playlist.itemCount} videos</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'liked' && (
            <>
              <h3>Liked Videos</h3>
              <button 
                className="play-all-btn"
                onClick={() => playAll(likedVideos)}
                disabled={likedVideos.length === 0}
              >
                ▶ Play All
              </button>
              <div className="video-list">
                {likedVideos.map(video => (
                  <div
                    key={video.id}
                    className={`video-item ${currentVideo?.id === video.id ? 'playing' : ''}`}
                    onClick={() => playVideo(video, likedVideos)}
                  >
                    <img 
                      src={video.thumbnail || '/placeholder-video.png'} 
                      alt="" 
                      className="video-thumb"
                    />
                    <div className="video-info">
                      <span className="video-title">{video.title}</span>
                      <span className="video-artist">{video.artist}</span>
                    </div>
                    {currentVideo?.id === video.id && (
                      <span className="now-playing">🎵</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'search' && (
            <>
              <h3>Search Results</h3>
              <div className="video-list">
                {searchResults.map(video => (
                  <div
                    key={video.id}
                    className={`video-item ${currentVideo?.id === video.id ? 'playing' : ''}`}
                    onClick={() => playVideo(video, searchResults)}
                  >
                    <img 
                      src={video.thumbnail || '/placeholder-video.png'} 
                      alt="" 
                      className="video-thumb"
                    />
                    <div className="video-info">
                      <span className="video-title">{video.title}</span>
                      <span className="video-artist">{video.artist}</span>
                    </div>
                    {currentVideo?.id === video.id && (
                      <span className="now-playing">🎵</span>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        {/* Main Area: Playlist Items or Player */}
        <div className="youtube-main">
          {activeTab === 'playlists' && selectedPlaylist && (
            <div className="playlist-detail">
              <div className="playlist-header">
                <img 
                  src={selectedPlaylist.thumbnail} 
                  alt="" 
                  className="playlist-cover"
                />
                <div className="playlist-meta">
                  <h2>{selectedPlaylist.title}</h2>
                  <p>{selectedPlaylist.itemCount} videos</p>
                  <button 
                    className="play-all-btn"
                    onClick={() => playAll(playlistItems)}
                    disabled={playlistItems.length === 0}
                  >
                    ▶ Play All
                  </button>
                </div>
              </div>

              {loadingItems ? (
                <div className="loading-items">Loading videos...</div>
              ) : (
                <div className="video-list">
                  {playlistItems.map((video, index) => (
                    <div
                      key={video.playlistItemId || video.id}
                      className={`video-item ${currentVideo?.id === video.id ? 'playing' : ''}`}
                      onClick={() => playVideo(video, playlistItems)}
                    >
                      <span className="video-index">{index + 1}</span>
                      <img 
                        src={video.thumbnail || '/placeholder-video.png'} 
                        alt="" 
                        className="video-thumb"
                      />
                      <div className="video-info">
                        <span className="video-title">{video.title}</span>
                        <span className="video-artist">{video.artist}</span>
                      </div>
                      {currentVideo?.id === video.id && (
                        <span className="now-playing">🎵</span>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'playlists' && !selectedPlaylist && (
            <div className="no-selection">
              <p>👈 Select a playlist to view its contents</p>
            </div>
          )}

          {(activeTab === 'liked' || activeTab === 'search') && (
            <div className="player-area">
              {currentVideo ? (
                <YouTubePlayer
                  videoId={currentVideo.id}
                  isPlaying={isPlaying}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onEnded={handleNext}
                  onNext={queueIndex < queue.length - 1 ? handleNext : null}
                  onPrevious={queueIndex > 0 ? handlePrevious : null}
                  currentTrack={currentVideo}
                  queueLength={queue.length}
                  currentIndex={queueIndex}
                />
              ) : (
                <div className="no-video">
                  <p>🎬 Select a video to play</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Fixed Player at Bottom (when playing from playlists tab) */}
      {activeTab === 'playlists' && currentVideo && (
        <div className="youtube-fixed-player">
          <YouTubePlayer
            videoId={currentVideo.id}
            isPlaying={isPlaying}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={handleNext}
            onNext={queueIndex < queue.length - 1 ? handleNext : null}
            onPrevious={queueIndex > 0 ? handlePrevious : null}
            currentTrack={currentVideo}
            queueLength={queue.length}
            currentIndex={queueIndex}
          />
        </div>
      )}
    </div>
  );
}

export default YouTubeLibrary;



